import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import type {
  CvHealthArea,
  CvHealthFinding,
  CvHealthReceipt,
  CvHealthReport,
  CvHealthReportEnvelope,
  CvHealthStage,
  CvHealthTrigger,
} from '@tabbio/types';
import { useCvHealthReview } from '@/hooks/useCvHealthReview';
import { useLocale } from '@/hooks/useLocale';
import {
  applyCvHealthChanges,
  getCvHealthReview,
  startCvHealthReview,
  isCvHealthFeatureDisabled,
  undoCvHealthChanges,
} from '@/lib/cv-health-api';
import { countSuggestions, toCountBand, toScoreBand } from '@/lib/cv-health-model';
import { getErrorObservabilityProperties, trackProductEvent } from '@/lib/product-analytics';
import { fetchSubscriptionState } from '@/lib/subscription-api';
import { getIndividualPaidSubscriptionTier } from '@/lib/subscription-purchase';
import {
  getFixableFindings,
  getStageTextKey,
  getSuggestionFindings,
  isFixableFinding,
} from '@/components/cv-health/cv-health-copy';
import type { HealthCardAction } from '@/components/cv-health/health-props';
import { useOnboarding, type NextStepOptions } from './OnboardingProvider';
import { shouldSkipCvHealthForImport } from './onboarding-navigation';

/**
 * Behaviour of the onboarding CV Health step, shared by `StepCvHealth.tsx`
 * (native) and `StepCvHealth.web.tsx`; the twins only render.
 *
 *   mount after import ──▶ checking ──▶ report
 *   report ──Fix my CV (or Continue when nothing is fixable)──▶ plan chooser
 *     (choose-ai, soft, shown once) ──any way out──▶ back here: fixing ──▶ fixed
 *   report ──Skip──▶ this-is-you (no chooser, no writing: the CV stays as uploaded)
 *   fixed ──Undo──▶ report (of the written CV)
 *   fixed ──Continue──▶ this-is-you
 *
 * Two ways on from a readable report: Fix (the capsule, or the solid
 * Continue when nothing is fixable: the writing still happens there) and a
 * quiet Skip. Fix is the one conversion moment of onboarding: the soft plan
 * chooser shows once in front of it, then the round writes, fixes and
 * rechecks the CV, so onboarding ends on the report of the CV the editor
 * opens. Skip leaves the CV exactly as uploaded, so the editor's report
 * matches the one the user saw. A failed check and an unreadable import
 * leave through "Continue without checking" (the writing still runs).
 *
 *   - `checking`: the honest check. The review judges the upload exactly as
 *     parsed (`source: 'import'`, the server's ResumeSource.raw), so the first
 *     score is the score of the user's own CV, never of the AI rewrite. The
 *     writing pass never runs on this path: Fix makes only the changes the
 *     report promises. Four named steps and
 *     a line of real progress; when it settles the label reads "Preparing
 *     your results" (~400 ms), then "Your CV Health is ready" (~900 ms),
 *     then the report. Reduced motion skips both dwells.
 *   - `report`: the score card (gauge, "N quick fixes will make it
 *     stronger.", the capsule "Fix my CV" when anything is fixable; a solid
 *     Continue in the footer when nothing is), the quiet Skip under it,
 *     "View individual suggestions" (read-only rows: the problem, then what
 *     we'll do), "What we checked" (each audience opens its tab, read-only).
 *     Nothing is edited or ticked here: the per-change review lives in the
 *     editor's sheet.
 *   - "Fix my CV" / that Continue is the soft conversion moment: the pending
 *     fix is recorded on the provider and the plan chooser shows (the
 *     provider's `nextStep`). Every way out of the chooser comes back here
 *     in phase `fixing`; a user already on Plus or Pro, or one who passed
 *     the chooser before (after an Undo), starts the round right here. Never
 *     a second paywall.
 *   - `fixing`: the SAME report layout (the card keeps the score the user
 *     saw; no capsule, no lists) with one agent line under it (design
 *     `.cvh-agent-bar`: the working heart, what the agent is doing, one
 *     muted context line). No heading, no checklist, no bar. No full
 *     rewrite of the CV: the fix makes only the changes the report promised.
 *     Two stages:
 *       1 "Fixing what we found…"   reviews the user's own CV
 *         (`onboarding_recheck`, unchanged since the check, so the same AI
 *         pass and the same fixes) and applies every fixable proposal of THAT
 *         report in one call (an import report is never applied); zero
 *         fixables skips the apply;
 *       2 "Checking your new CV…"   follows the recheck the apply returned.
 *     Never worse: a recheck below the score the user was shown is undone
 *     automatically and the result says the CV already scores that.
 *     An Undo shows the same layout with "Putting your original text back…".
 *   - `fixed`: the gauge moves from the import score to the new one, "Your
 *     CV went from 52 to 88." (or "Your CV now scores 88."), "N changes
 *     saved · Undo", and, when questions remain, one calm line about the
 *     details only the user can add. Continue.
 *   A failed check offers Try again and Continue; a failed fix says so with
 *   Try again (the fix runs again from its first stage; a fixed CV has nothing
 *   left to fix, so a repeat is harmless) and Continue. Never a dead end.
 *
 * The provider keeps the import report id and score, the fix (its report id
 * and receipt) and the phase, so a remount (Back from this-is-you, browser
 * Back, a web reload, a checkout return) adopts what it had instead of
 * checking again; a reload mid-fix restarts the fix from its first stage. A
 * disabled feature skips the step (the writing pass starts then), and so
 * does a typed description that is not a real CV (nothing to check: the
 * writing pass, kicked at import, builds it). The writing pass never runs on
 * a CV that went through this step's report.
 *
 * On native the plan chooser (choose-ai) is a sheet over this step: the
 * wizard renders this component for choose-ai too (`isBackdrop`). As a
 * backdrop it only shows a report it already has; it never starts a review,
 * never changes the CV and never navigates.
 */

const PREPARING_DWELL_MS = 400;
const READY_DWELL_MS = 900;

/** Report ids whose `cv_health_report_viewed` already fired this session. */
const viewedReportIds = new Set<string>();

export type CvHealthStepView =
  /** Render nothing: skipping (feature off, nothing to check), or a backdrop with no report. */
  | 'hidden'
  | 'checking'
  | 'failed'
  /** A known report is being fetched after a remount. */
  | 'loading'
  | 'unreadable'
  | 'report'
  /** The fix round or an Undo: the report layout with the agent line under the card. */
  | 'fixing'
  | 'fixed';

type SettleStep = 'none' | 'preparing' | 'ready';

/** `writing` waits for the writing pass; the rest are the server's work. */
type FixStage = 'idle' | 'reviewing' | 'applying' | 'rechecking';

type CompletionAction = NonNullable<NextStepOptions['completionAction']>;

export interface CvHealthStepFooterAction {
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export interface CvHealthStepFooter {
  /** The solid button; none while the card's own action leads. */
  primary: CvHealthStepFooterAction | null;
  /** The quiet link under the primary button. */
  secondary: CvHealthStepFooterAction | null;
}

export interface OnboardingCvHealthStep {
  view: CvHealthStepView;
  /** Native only: rendered behind the choose-ai sheet. Actions are inert. */
  isBackdrop: boolean;
  /** Centred nav title: "Checking your CV", "Fixing your CV" or "CV Health". */
  title: string;
  /** Whether the nav bar shows the back chevron (never while working). */
  canGoBack: boolean;
  goBack: () => void;

  // Checking (the four steps)
  /** The live stage line, also the screen reader's label for the progress. */
  statusText: string;
  checkingReport: CvHealthReport | null;
  stage: CvHealthStage;

  // Working (the fix round, or an Undo): the agent line under the card
  /** What the agent is doing right now, with one optional context line. */
  agent: { label: string; context: string | null } | null;

  // Report and fixed (one layout, so the gauge moves between them)
  /** The report the card and lists show. */
  report: CvHealthReport | null;
  /** The import score: where the fixed gauge moves from (also after a reload). */
  fromScore: number | null;
  /** Inside the report: the overview or one audience's tabs. */
  reportView: 'overview' | 'area';
  area: CvHealthArea;
  openArea: (area: CvHealthArea) => void;
  setArea: (area: CvHealthArea) => void;
  backToOverview: () => void;
  /** The card's capsule: "Fix my CV". Null when nothing is fixable. */
  cardAction: HealthCardAction | null;
  /** Under the card on the report: "3 quick fixes will make it stronger." */
  fixesLine: string | null;
  /** Under the card once fixed: "Your CV went from 52 to 88." */
  resultHeading: string | null;
  /** Rows of "View individual suggestions" (the report only; read-only). */
  suggestions: CvHealthFinding[];
  /** "View individual suggestions", controlled so a remount can keep it. */
  suggestionsOpen: boolean;
  setSuggestionsOpen: (open: boolean) => void;
  /** "3 changes saved" once fixed, with Undo while the receipt is held. */
  savedText: string | null;
  undo: (() => void) | null;
  undoing: boolean;
  /** One calm line: "2 things only you can add. Add them anytime in your CV." */
  note: string | null;
  /** An inline error: the fix or the undo did not go through. */
  error: string | null;
  footer: CvHealthStepFooter | null;

  // Failed and unreadable
  retry: () => void;
  continueWithoutChecking: () => void;
  tryAnotherFile: () => void;

  copy: {
    tryAgain: string;
    continueWithout: string;
    tryAnotherFile: string;
    back: string;
    failed: string;
    undo: string;
    viewSuggestions: string;
    whatWeChecked: string;
  };
}

export function useOnboardingCvHealth({
  reduceMotion,
}: {
  /** Reanimated `useReducedMotion()` natively, `prefers-reduced-motion` on web. */
  reduceMotion: boolean;
}): OnboardingCvHealthStep {
  const { t } = useTranslation(['auth', 'common']);
  const { locale } = useLocale();
  const {
    stepName,
    nextStep,
    prevStep,
    cvHealthImport,
    cvHealthPhase,
    setCvHealthPhase,
    cvHealthReportId,
    cvHealthImportScore,
    setCvHealthImportReport,
    cvHealthFix,
    setCvHealthFix,
    refreshCvSectionsFromServer,
    setCvHealthPendingFix,
    cvHealthChooserDone,
    markCvHealthChooserDone,
    ensureCvEnrichmentStarted,
  } = useOnboarding();

  // Only native draws this step behind the chooser sheet. On web the chooser
  // is its own route, and the provider's step name lags the route by a
  // render on the way back: reading it there made the returning step think
  // it was a backdrop and adopt the old report beside the fix round.
  const isBackdrop = Platform.OS !== 'web' && stepName === 'choose-ai';
  const skipImport = shouldSkipCvHealthForImport(cvHealthImport);
  /** Back from the plan chooser (or a reload mid-fix): the fix runs now. */
  const fixDue = !isBackdrop && cvHealthPhase === 'fixing';

  const [localReport, setLocalReport] = useState<CvHealthReport | null>(null);
  const [settleStep, setSettleStep] = useState<SettleStep>('none');
  const [disabled, setDisabled] = useState(false);
  const [restoreFailed, setRestoreFailed] = useState(false);
  const [fixStage, setFixStageState] = useState<FixStage>('idle');
  const [undoing, setUndoingState] = useState(false);
  const [error, setError] = useState<'fix' | 'undo' | null>(null);
  const [reportView, setReportView] = useState<'overview' | 'area'>('overview');
  const [area, setArea] = useState<CvHealthArea>('human');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  /** "Fix my CV" is waiting for the plan check. */
  const [requestingFix, setRequestingFix] = useState(false);

  const mountedRef = useRef(false);
  const initializedRef = useRef(false);
  /** The settle that follows a start() of the honest check plays the dwells. */
  const freshRunRef = useRef(false);
  /** The settle that follows a remount's adopt of the fixed report. */
  const restoringFixedRef = useRef(false);
  const advancingRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const fixStageRef = useRef<FixStage>('idle');
  const undoingRef = useRef(false);
  /** The receipt the apply returned, until the recheck settles and the provider keeps it. */
  const receiptRef = useRef<CvHealthReceipt | null>(null);
  const requestingFixRef = useRef(false);
  const reduceMotionRef = useRef(reduceMotion);
  reduceMotionRef.current = reduceMotion;
  const isBackdropRef = useRef(isBackdrop);
  isBackdropRef.current = isBackdrop;
  const localReportRef = useRef(localReport);
  localReportRef.current = localReport;
  const importScoreRef = useRef(cvHealthImportScore);
  importScoreRef.current = cvHealthImportScore;
  const fixRef = useRef(cvHealthFix);
  fixRef.current = cvHealthFix;
  const chooserDoneRef = useRef(cvHealthChooserDone);
  chooserDoneRef.current = cvHealthChooserDone;
  /** The step's view as last rendered (assigned once it is computed below). */
  const viewRef = useRef<CvHealthStepView>('hidden');
  /**
   * Already on Plus or Pro (null until known): the plan chooser has nothing
   * to offer and passes itself, so "Fix my CV" starts right here. The app's
   * subscription query only runs after onboarding, so the step asks the
   * server itself, once, while the user reads the report.
   */
  const alreadyPaidRef = useRef<boolean | null>(null);
  const alreadyPaidRequestRef = useRef<Promise<boolean> | null>(null);

  const setFixStage = useCallback((next: FixStage) => {
    fixStageRef.current = next;
    setFixStageState(next);
  }, []);

  const setUndoing = useCallback((next: boolean) => {
    undoingRef.current = next;
    setUndoingState(next);
  }, []);

  const clearTimers = useCallback(() => {
    for (const timer of timersRef.current) clearTimeout(timer);
    timersRef.current = [];
  }, []);

  const later = useCallback((ms: number, run: () => void) => {
    const timer = setTimeout(() => {
      timersRef.current = timersRef.current.filter((entry) => entry !== timer);
      if (mountedRef.current) run();
    }, ms);
    timersRef.current.push(timer);
  }, []);

  const advance = useCallback(
    (completionAction: CompletionAction, historyMode?: NextStepOptions['historyMode']) => {
      if (isBackdropRef.current || advancingRef.current) return;
      advancingRef.current = true;
      void nextStep({ completionAction, historyMode });
    },
    [nextStep],
  );

  // A new step (native keeps this mounted from cv-health into choose-ai and
  // back) re-arms the double-tap guard.
  useEffect(() => {
    advancingRef.current = false;
  }, [stepName]);

  /** The honest check settled: the provider remembers it, the report shows. */
  const commitReport = useCallback(
    (report: CvHealthReport) => {
      setSettleStep('none');
      setCvHealthImportReport({ id: report.id, score: report.score });
      setCvHealthPhase('report');
    },
    [setCvHealthImportReport, setCvHealthPhase],
  );

  // ── The fix round (set below; referenced from the settle handler) ───────
  const applyAllRef = useRef<(written: CvHealthReport) => void>(() => {});
  const finishFixRef = useRef<(report: CvHealthReport) => void>(() => {});
  const finishUndoRef = useRef<(report: CvHealthReport) => void>(() => {});

  const handleSettled = (envelope: CvHealthReportEnvelope) => {
    const report = envelope.report;
    const fresh = freshRunRef.current;
    freshRunRef.current = false;
    clearTimers();
    // Stage 2 settled: the written CV's own report, fixed in one call.
    if (fixStageRef.current === 'reviewing') {
      applyAllRef.current(report);
      return;
    }
    // Stage 3 settled: the recheck after the apply.
    if (fixStageRef.current === 'rechecking') {
      finishFixRef.current(report);
      return;
    }
    if (undoingRef.current) {
      finishUndoRef.current(report);
      return;
    }
    if (restoringFixedRef.current) {
      // A remount of the fixed report: the provider already holds the fix.
      restoringFixedRef.current = false;
      setLocalReport(report);
      return;
    }
    setLocalReport(report);
    // An adopted report, an unreadable CV (nothing to settle) and reduced
    // motion go straight to the report: the check never replays.
    if (!fresh || report.status === 'unreadable' || reduceMotionRef.current) {
      commitReport(report);
      return;
    }
    setSettleStep('preparing');
    later(PREPARING_DWELL_MS, () => {
      setSettleStep('ready');
      later(READY_DWELL_MS, () => commitReport(report));
    });
  };

  const handleDisabled = () => {
    setDisabled(true);
    // Nothing to check, so nothing waited for: the writing pass starts now
    // (as it would have at import), at the tier the server resolves.
    ensureCvEnrichmentStarted();
    // Skip, and replace the history entry so browser Back from this-is-you
    // lands on ai-input instead of bouncing forward through this step.
    advance('skip_disabled', 'replace');
  };
  const handleDisabledRef = useRef(handleDisabled);
  handleDisabledRef.current = handleDisabled;

  const review = useCvHealthReview({
    onSettled: handleSettled,
    onDisabled: handleDisabled,
  });
  const { start, adopt, phase: reviewPhase, errorCode } = review;

  /** The honest check of the upload as parsed (and its retry). */
  const startReview = useCallback(
    (trigger: CvHealthTrigger) => {
      clearTimers();
      setSettleStep('none');
      setRestoreFailed(false);
      setLocalReport(null);
      setError(null);
      freshRunRef.current = true;
      trackProductEvent('cv_health_review_started', {
        entry_point: 'onboarding',
        trigger,
        source: 'import',
        import_source: cvHealthImport?.source ?? 'unknown',
      });
      start({
        target: 'master',
        trigger,
        source: 'import',
        locale: locale === 'ar' ? 'ar' : 'en',
        importSignals: cvHealthImport?.quality ?? null,
        cvHealthToken: cvHealthImport?.cvHealthToken ?? null,
      });
    },
    [clearTimers, cvHealthImport, locale, start],
  );

  const failRestore = (code: string) => {
    trackProductEvent('cv_health_review_failed', {
      entry_point: 'onboarding',
      error_code: code,
      during: 'restore',
    });
    setRestoreFailed(true);
  };

  /** Fetch and adopt a known report; `fallback` runs when it is gone. */
  const restore = (reportId: string, fallback: (() => void) | null) => {
    void getCvHealthReview(reportId).then(
      (response) => {
        if (!mountedRef.current) return;
        if (response.data) {
          adopt(response.data);
          return;
        }
        if (isCvHealthFeatureDisabled(response)) {
          handleDisabledRef.current();
          return;
        }
        if (fallback) fallback();
        else failRestore(response.error?.code ?? 'REVIEW_RESTORE_FAILED');
      },
      () => {
        if (!mountedRef.current) return;
        if (fallback) fallback();
        else failRestore('REVIEW_RESTORE_FAILED');
      },
    );
  };

  /** Display-only fetch for the card during a fix; a settled round wins. */
  const showReportWhileFixing = (reportId: string) => {
    void getCvHealthReview(reportId).then(
      (response) => {
        if (!mountedRef.current || localReportRef.current || !response.data) return;
        setLocalReport(response.data.report);
      },
      () => undefined,
    );
  };

  // Mount: skip, adopt a known report, or start the check. Once per mount.
  const initialize = () => {
    if (skipImport) {
      trackProductEvent('cv_health_review_skipped', {
        entry_point: 'onboarding',
        reason: 'typed_description',
      });
      handleDisabledRef.current();
      return;
    }
    // Back from the plan chooser (web remounts this step), or a reload
    // mid-fix: the fix effect below runs it from its first stage. The card
    // meanwhile shows the report the user just saw, fetched for display only
    // (never adopted, so it cannot settle into the round); without it the
    // whole step was blank while the fix ran.
    if (fixDue) {
      if (cvHealthReportId) showReportWhileFixing(cvHealthReportId);
      return;
    }
    if (cvHealthPhase === 'fixed' && cvHealthFix) {
      restoringFixedRef.current = true;
      restore(cvHealthFix.reportId, () => {
        // The fixed report is gone: forget the fix, show the honest report.
        restoringFixedRef.current = false;
        setCvHealthFix(null);
        if (cvHealthReportId) restore(cvHealthReportId, null);
        else failRestore('REVIEW_RESTORE_FAILED');
      });
      return;
    }
    if (cvHealthReportId) {
      restore(cvHealthReportId, null);
      return;
    }
    // Behind the chooser there is nothing to show without a report.
    if (isBackdrop) return;
    if (cvHealthPhase !== 'checking') setCvHealthPhase('checking');
    if (reviewPhase === 'idle') startReview('onboarding_import');
  };
  const initializeRef = useRef(initialize);
  initializeRef.current = initialize;

  useEffect(() => {
    mountedRef.current = true;
    if (!initializedRef.current) {
      initializedRef.current = true;
      initializeRef.current();
    }
    return () => {
      mountedRef.current = false;
      clearTimers();
    };
  }, [clearTimers]);

  // ── The fix round ────────────────────────────────────────────────────

  /** Any stage failed: say so on the report; Try again runs the round again. */
  const failFix = () => {
    clearTimers();
    receiptRef.current = null;
    setFixStage('idle');
    setError('fix');
    setCvHealthPendingFix(false);
    setCvHealthPhase('report');
  };
  const failFixRef = useRef(failFix);
  failFixRef.current = failFix;

  /**
   * Stage 1: review the CV as it is now (`onboarding_recheck`: the user's own
   * CV, unchanged since the check, with the import signals still honoured;
   * never the `import` source, whose report cannot be applied). The same text
   * gives the same AI pass, so these are the fixes the report promised.
   */
  const reviewCurrent = () => {
    clearTimers();
    setFixStage('reviewing');
    freshRunRef.current = false;
    start({
      target: 'master',
      trigger: 'onboarding_recheck',
      locale: locale === 'ar' ? 'ar' : 'en',
      importSignals: cvHealthImport?.quality ?? null,
    });
  };
  const reviewCurrentRef = useRef(reviewCurrent);
  reviewCurrentRef.current = reviewCurrent;

  /**
   * "Fix my CV": the targeted, validated fixes the report promised, applied
   * to the user's own CV. No full rewrite: one could lower the score the user
   * was just shown (owner testing, 2026-10-04: 92 → 86 on Free) or leave the
   * promised fix undone. A paid pick on the chooser upgrades the model the
   * fixes are drafted with; it never rewrites the CV.
   */
  const startFix = () => {
    // One round at a time: a double tap on Try again must not start a second.
    if (isBackdropRef.current || fixStageRef.current !== 'idle' || undoingRef.current) return;
    clearTimers();
    receiptRef.current = null;
    setError(null);
    setRestoreFailed(false);
    // Try again can be tapped from an area tab; the round ends on the overview.
    setReportView('overview');
    setCvHealthPhase('fixing');
    trackProductEvent('cv_health_fix_started', { entry_point: 'onboarding' });
    reviewCurrentRef.current();
  };
  const startFixRef = useRef(startFix);
  startFixRef.current = startFix;

  // Arrival from the plan chooser (a web mount, native's sheet sliding away
  // over this step) or a reload mid-fix: run the fix the user asked for.
  useEffect(() => {
    if (!fixDue || !mountedRef.current) return;
    startFixRef.current();
  }, [fixDue]);

  /** Stage 1 settled: apply every fixable proposal of the CV's own report. */
  const applyAll = (written: CvHealthReport) => {
    if (fixStageRef.current !== 'reviewing') return;
    if (written.status === 'unreadable') {
      failFixRef.current();
      return;
    }
    const fixable = getFixableFindings(written);
    if (fixable.length === 0) {
      // Nothing the fix can change: the CV's own report is the result.
      finishFixRef.current(written);
      return;
    }
    setFixStage('applying');
    void applyCvHealthChanges({
      reportId: written.id,
      items: fixable.map((finding) => ({ findingKey: finding.key })),
      trigger: 'onboarding_fix',
    })
      .then(
        (response) => response.data ?? null,
        () => null,
      )
      .then((result) => {
        if (!mountedRef.current || fixStageRef.current !== 'applying') return;
        if (!result) {
          failFixRef.current();
          return;
        }
        receiptRef.current = result.receipt;
        // Stage 3: the recheck the apply returned (the base report itself
        // when nothing was written; it is already settled).
        setFixStage('rechecking');
        adopt(result);
      });
  };
  applyAllRef.current = applyAll;

  /** Stage 3 settled: the fixed CV's report, kept on the provider with the receipt. */
  /** Pull the CV the server now holds into the onboarding draft; never throws. */
  const syncCvFromServer = (after: 'fix' | 'undo') =>
    refreshCvSectionsFromServer().catch((error: unknown) => {
      trackProductEvent('cv_health_cv_sync_failed', {
        entry_point: 'onboarding',
        after,
        ...getErrorObservabilityProperties(error),
      });
    });

  /** A fix that scored lower than the CV did: put it back and say so plainly. */
  const revertLowerFix = (receipt: CvHealthReceipt, lower: CvHealthReport) => {
    trackProductEvent('cv_health_fix_reverted', {
      entry_point: 'onboarding',
      from_band: toScoreBand(importScoreRef.current),
      to_band: toScoreBand(lower.score),
      applied_count: receipt.items.length,
    });
    void undoCvHealthChanges({ reportId: receipt.fromReportId, receipt, trigger: 'onboarding_undo' })
      .then(
        (response) => response.data?.report ?? null,
        () => null,
      )
      .then((restored) => {
        const shown = restored ?? lower;
        void syncCvFromServer('undo').finally(() => {
          if (!mountedRef.current) return;
          setLocalReport(shown);
          setFixStage('idle');
          setError(null);
          setCvHealthFix({ reportId: shown.id, receipt: null });
        });
      });
  };

  const finishFix = (report: CvHealthReport) => {
    clearTimers();
    const receipt = receiptRef.current;
    receiptRef.current = null;
    const applied = receipt?.items.length ?? 0;
    // Never worse: "Fix my CV" must not end below the score the user was
    // shown. Undo the changes and keep their CV as it was.
    const shown = importScoreRef.current;
    if (
      receipt &&
      applied > 0 &&
      typeof shown === 'number' &&
      typeof report.score === 'number' &&
      report.score < shown
    ) {
      revertLowerFix(receipt, report);
      return;
    }
    trackProductEvent('cv_health_fix_completed', {
      entry_point: 'onboarding',
      applied_count: applied,
      skipped_count: receipt?.skipped.length ?? 0,
      from_band: toScoreBand(importScoreRef.current),
      to_band: toScoreBand(report.score),
    });
    const commit = () => {
      if (!mountedRef.current) return;
      setLocalReport(report);
      setFixStage('idle');
      setError(null);
      setCvHealthFix({ reportId: report.id, receipt: applied > 0 ? receipt : null });
    };
    if (applied === 0) {
      commit();
      return;
    }
    // The server changed the CV: the in-memory copy must follow before the
    // result shows, or how-to-reach-you saves the pre-fix text back over it.
    void syncCvFromServer('fix').finally(commit);
  };
  finishFixRef.current = finishFix;

  const finishUndo = (report: CvHealthReport) => {
    void syncCvFromServer('undo').finally(() => {
      if (!mountedRef.current) return;
      setUndoing(false);
      setLocalReport(report);
      // Back to a report (of the written CV): "Fix my CV" runs right here, the
      // chooser was passed.
      setCvHealthFix(null);
    });
  };
  finishUndoRef.current = finishUndo;

  const undoFix = useCallback(() => {
    const fix = fixRef.current;
    if (
      isBackdropRef.current ||
      !fix?.receipt ||
      fixStageRef.current !== 'idle' ||
      undoingRef.current
    ) {
      return;
    }
    const receipt = fix.receipt;
    setError(null);
    setReportView('overview');
    setUndoing(true);
    void undoCvHealthChanges({ reportId: receipt.fromReportId, receipt, trigger: 'onboarding_undo' })
      .then(
        (response) => response.data ?? null,
        () => null,
      )
      .then((result) => {
        if (!mountedRef.current) return;
        if (!result) {
          setUndoing(false);
          setError('undo');
          return;
        }
        trackProductEvent('cv_health_fix_undone', {
          entry_point: 'onboarding',
          applied_count: receipt.items.length,
        });
        adopt(result);
      });
  }, [adopt, setUndoing]);

  const checkAlreadyPaid = useCallback((): Promise<boolean> => {
    if (alreadyPaidRef.current !== null) return Promise.resolve(alreadyPaidRef.current);
    alreadyPaidRequestRef.current ??= fetchSubscriptionState({ mode: 'seeker' }).then(
      (result) => {
        alreadyPaidRef.current = getIndividualPaidSubscriptionTier(result) !== null;
        return alreadyPaidRef.current;
      },
      () => {
        alreadyPaidRequestRef.current = null;
        return false;
      },
    );
    return alreadyPaidRequestRef.current;
  }, []);

  /**
   * "Fix my CV" on the report (and the Continue that stands in for it when
   * nothing is fixable). The first time it is the soft conversion moment:
   * the pending fix is recorded and the plan chooser shows; every way out of
   * it brings the user back here to the fix (the provider's `nextStep`).
   * Once the chooser was passed (or for Plus and Pro, who have no chooser
   * to see) the fix starts right here.
   */
  const requestFix = async () => {
    if (
      requestingFixRef.current ||
      isBackdropRef.current ||
      advancingRef.current ||
      fixStageRef.current !== 'idle' ||
      undoingRef.current
    ) {
      return;
    }
    const current = localReportRef.current;
    if (!current) return;
    // Nothing fixable is still a round (Continue): the writing pass happens
    // here regardless, and the editor must show the report of that CV.
    const fixable = getFixableFindings(current);
    trackProductEvent('cv_health_fix_requested', {
      entry_point: 'onboarding',
      count_band: toCountBand(fixable.length),
      score_band: toScoreBand(current.score),
    });
    requestingFixRef.current = true;
    setRequestingFix(true);
    const alreadyPaid = await checkAlreadyPaid();
    requestingFixRef.current = false;
    setRequestingFix(false);
    if (!mountedRef.current || isBackdropRef.current || fixStageRef.current !== 'idle') return;
    if (chooserDoneRef.current || alreadyPaid) {
      // Plus or Pro has no chooser to show (the server writes premium).
      if (alreadyPaid) markCvHealthChooserDone();
      startFixRef.current();
      return;
    }
    setCvHealthPendingFix(true);
    advance('fix_with_ai');
  };
  const requestFixRef = useRef(requestFix);
  requestFixRef.current = requestFix;

  const retryFix = useCallback(() => {
    if (isBackdropRef.current) return;
    startFixRef.current();
  }, []);

  // Hook failures (start, poll timeout, failed report). Restore failures are
  // tracked where they happen. A failure inside the fix round ends it.
  useEffect(() => {
    if (reviewPhase !== 'failed') return;
    const during = fixStageRef.current !== 'idle' ? 'fix' : undoingRef.current ? 'undo' : 'check';
    trackProductEvent('cv_health_review_failed', {
      entry_point: 'onboarding',
      error_code: errorCode ?? 'REVIEW_FAILED',
      during,
    });
    if (during === 'fix') {
      failFixRef.current();
    } else if (during === 'undo') {
      setUndoing(false);
      setError('undo');
    }
  }, [errorCode, reviewPhase, setUndoing]);

  // ── The report's views ───────────────────────────────────────────────
  const openArea = useCallback((next: CvHealthArea) => {
    // The report and the fixed report show the tabs; never while working.
    if (viewRef.current !== 'report' && viewRef.current !== 'fixed') return;
    setArea(next);
    setReportView('area');
  }, []);

  const backToOverview = useCallback(() => setReportView('overview'), []);

  // ── Navigation ───────────────────────────────────────────────────────
  // The provider clears a pending fix whenever the user leaves this step
  // (Skip, Continue, Back), and routes every exit but Fix around the chooser.
  const goBack = useCallback(() => {
    if (isBackdropRef.current) return;
    prevStep();
  }, [prevStep]);

  const continueToNext = useCallback(() => advance('continue'), [advance]);
  /**
   * Skip: no chooser and NO writing pass. The CV stays exactly as uploaded,
   * so the report the editor opens matches the one the user just saw. The
   * editor never reads the upload's report (it describes the parse, not the
   * CV), so the CV as it now is gets its own review in the background; with
   * the same text the AI pass is the same and so are the findings.
   */
  const skipReport = useCallback(() => {
    void startCvHealthReview({
      target: 'master',
      trigger: 'onboarding_recheck',
      locale: locale === 'ar' ? 'ar' : 'en',
      importSignals: cvHealthImport?.quality ?? null,
    }).catch(() => undefined);
    advance('skip');
  }, [advance, cvHealthImport, locale]);
  const continueWithoutChecking = useCallback(() => {
    // No report to end on, so nothing to keep in step: the writing pass
    // starts now, as it would have at import.
    if (isBackdropRef.current) return;
    ensureCvEnrichmentStarted();
    advance('continue_without_checking');
  }, [advance, ensureCvEnrichmentStarted]);
  const retryReview = useCallback(() => {
    if (isBackdropRef.current) return;
    setCvHealthImportReport(null);
    setCvHealthPhase('checking');
    startReview('onboarding_retry');
  }, [setCvHealthImportReport, setCvHealthPhase, startReview]);
  // Back to the import composer: the next import starts a fresh check.
  const tryAnotherFile = useCallback(() => {
    if (isBackdropRef.current) return;
    prevStep();
  }, [prevStep]);

  // ── View ─────────────────────────────────────────────────────────────
  const report = localReport;

  let view: CvHealthStepView;
  if (disabled || skipImport) {
    view = 'hidden';
  } else if (restoreFailed || (reviewPhase === 'failed' && !report) || (error === 'fix' && !report)) {
    view = 'failed';
  } else if (fixStage !== 'idle' || undoing) {
    view = 'fixing';
  } else if (cvHealthPhase === 'checking') {
    view = 'checking';
  } else if (!report) {
    view = 'loading';
  } else if (report.status === 'unreadable') {
    view = 'unreadable';
  } else if (cvHealthPhase === 'fixed' && cvHealthFix) {
    view = 'fixed';
  } else {
    view = 'report';
  }
  if (isBackdrop && view !== 'report' && view !== 'fixed' && view !== 'unreadable') {
    view = 'hidden';
  }
  viewRef.current = view;

  // Learn the plan while the user reads the report, so the fix never waits on it.
  useEffect(() => {
    if (isBackdrop || view !== 'report') return;
    void checkAlreadyPaid();
  }, [checkAlreadyPaid, isBackdrop, view]);

  useEffect(() => {
    if (isBackdrop || !report || (view !== 'report' && view !== 'fixed' && view !== 'unreadable')) {
      return;
    }
    if (viewedReportIds.has(report.id)) return;
    viewedReportIds.add(report.id);
    trackProductEvent('cv_health_report_viewed', {
      entry_point: 'onboarding',
      status: report.status,
      source: report.source ?? 'unknown',
      fixed: view === 'fixed',
      score_band: toScoreBand(report.score),
      count_band: toCountBand(countSuggestions(report)),
      ai_status: report.aiPass?.status ?? 'none',
    });
  }, [isBackdrop, report, view]);

  // Checking: the live report while it runs; the settled one during the dwells.
  const checkingReport = review.report;
  let stage: CvHealthStage = 'reading';
  if (settleStep === 'preparing') stage = 'preparing';
  else if (settleStep === 'ready') stage = 'ready';
  else if (reviewPhase === 'running') stage = review.stage;
  const statusText = t(`common:${getStageTextKey(stage)}`);

  // Working: what the agent is doing, said once under the card (the design's
  // agent bar). Never a checklist, a bar or a timer.
  let agent: OnboardingCvHealthStep['agent'] = null;
  if (undoing) {
    agent = { label: t('auth:onboarding.cvHealth.agent.undoing'), context: null };
  } else if (fixStage === 'reviewing' || fixStage === 'applying') {
    agent = {
      label: t('auth:onboarding.cvHealth.agent.fixing'),
      context: t('auth:onboarding.cvHealth.agent.fixingContext'),
    };
  } else if (fixStage === 'rechecking') {
    agent = {
      label: t('auth:onboarding.cvHealth.agent.rechecking'),
      context: t('auth:onboarding.cvHealth.agent.recheckingContext'),
    };
  }

  const fixableCount = report ? getFixableFindings(report).length : 0;
  const receipt = cvHealthFix?.receipt ?? null;
  const appliedCount = receipt?.items.length ?? 0;
  /** Open findings that need a detail only the user has (never auto-fixed). */
  const questionCount = report
    ? getSuggestionFindings(report).filter((finding) => !isFixableFinding(finding)).length
    : 0;

  const continueLabel = t('auth:onboarding.cvHealth.continue', { defaultValue: 'Continue' });
  const tryAgainLabel = t('auth:onboarding.cvHealth.tryAgain', { defaultValue: 'Try again' });
  const skipLabel = t('auth:onboarding.skip', { defaultValue: 'Skip' });
  const fixFailedText = t('auth:onboarding.cvHealth.fixFailed', {
    defaultValue: "We couldn't finish fixing your CV.",
  });

  let footer: CvHealthStepFooter | null = null;
  if (view === 'fixed') {
    footer = {
      primary: { label: continueLabel, onPress: continueToNext, disabled: undoing },
      secondary: null,
    };
  } else if (view === 'report' && report) {
    // The quiet way past the report: no chooser, no writing, the CV as uploaded.
    const skipAction: CvHealthStepFooterAction = {
      label: skipLabel,
      onPress: skipReport,
      disabled: requestingFix,
    };
    if (error === 'fix') {
      // A failed round is never a dead end: Try again, or leave.
      footer = {
        primary: { label: tryAgainLabel, onPress: retryFix },
        secondary: { label: continueLabel, onPress: continueToNext },
      };
    } else if (fixableCount === 0) {
      // Nothing to fix on the upload, but the writing pass still happens
      // here: Continue runs the same round (chooser once, the written CV
      // reviewed, its fixables applied, rechecked), so onboarding ends on
      // the report of the CV the editor will show. Skip stays available.
      footer = {
        primary: {
          label: continueLabel,
          onPress: () => {
            void requestFixRef.current();
          },
          loading: requestingFix,
        },
        secondary: skipAction,
      };
    } else {
      // Anything fixable: the card's "Fix my CV" leads; Skip sits under the report.
      footer = { primary: null, secondary: skipAction };
    }
  }

  // The report keeps its title while the agent works under the card.
  let title = t('auth:onboarding.cvHealth.reportTitle', { defaultValue: 'CV Health' });
  if (view === 'checking') {
    title = t('common:cvHealth.simple.checkingHeading');
  }

  let resultHeading: string | null = null;
  if (view === 'fixed' && report && typeof report.score === 'number') {
    const from = cvHealthImportScore;
    resultHeading =
      typeof from === 'number' && report.score > from
        ? t('auth:onboarding.cvHealth.fixedUp', { from, to: report.score })
        : typeof from === 'number' && report.score === from
          ? t('auth:onboarding.cvHealth.alreadyScores', { score: report.score })
          : t('auth:onboarding.cvHealth.fixedNow', { score: report.score });
  }

  return {
    view,
    isBackdrop,
    title,
    canGoBack:
      ((view === 'report' || view === 'fixed') && reportView === 'overview') ||
      view === 'unreadable' ||
      view === 'loading',
    goBack,
    statusText,
    checkingReport,
    stage,
    agent,
    report,
    fromScore: cvHealthImportScore,
    reportView: view === 'report' || view === 'fixed' ? reportView : 'overview',
    area,
    openArea,
    setArea,
    backToOverview,
    cardAction:
      view === 'report' && error !== 'fix' && fixableCount > 0
        ? {
            label: t('auth:onboarding.cvHealth.fixMyCv', { defaultValue: 'Fix my CV' }),
            onPress: () => {
              void requestFixRef.current();
            },
            busy: requestingFix,
          }
        : null,
    fixesLine:
      view === 'report' && fixableCount > 0
        ? t('common:cvHealth.simple.fixesSub', { count: fixableCount })
        : null,
    resultHeading,
    // The rows "Fix my CV" will change come first, so the top row matches
    // "N quick fixes"; rows that need the user's OK follow.
    suggestions:
      view === 'report' && report
        ? [...getSuggestionFindings(report)].sort(
            (a, b) => Number(isFixableFinding(b)) - Number(isFixableFinding(a)),
          )
        : [],
    suggestionsOpen,
    setSuggestionsOpen,
    savedText:
      view === 'fixed' && appliedCount > 0
        ? t('common:cvHealth.report.changesSaved', { count: appliedCount })
        : null,
    undo: view === 'fixed' && appliedCount > 0 && receipt ? undoFix : null,
    undoing,
    note:
      view === 'fixed' && questionCount > 0
        ? t('auth:onboarding.cvHealth.questionsLeft', { count: questionCount })
        : null,
    error:
      error === 'fix'
        ? fixFailedText
        : error === 'undo'
          ? t('common:cvHealth.heading.undoFailed')
          : null,
    footer,
    retry: error === 'fix' ? retryFix : retryReview,
    continueWithoutChecking: error === 'fix' ? continueToNext : continueWithoutChecking,
    tryAnotherFile,
    copy: {
      tryAgain: tryAgainLabel,
      continueWithout:
        error === 'fix'
          ? continueLabel
          : t('auth:onboarding.cvHealth.continueWithout', {
              defaultValue: 'Continue without checking',
            }),
      tryAnotherFile: t('auth:onboarding.cvHealth.tryAnotherFile', {
        defaultValue: 'Try another file',
      }),
      back: t('auth:onboarding.cvHealth.back', { defaultValue: 'Back' }),
      undo: t('common:cvHealth.simple.undo'),
      viewSuggestions: t('common:cvHealth.report.viewSuggestions'),
      whatWeChecked: t('common:cvHealth.report.whatWeChecked'),
      failed: error === 'fix' ? fixFailedText : t(`common:${getStageTextKey('failed')}`),
    },
  };
}
